import type { ReactElement } from 'react';
import React, { useMemo } from 'react';
import { format, isSameDay } from 'date-fns';
import { sortBy, uniqBy } from 'lodash';
import { Badge, PopoverBody } from 'reactstrap';
import { v4 as uuid } from 'uuid';
import BadgeList from 'atom-components/badge-list';
import ControlledPopover from 'atom-components/popover';
import { commonDateFormat, ordinalDateFormat } from 'helpers/date.helpers';
import { usePeriods } from 'services/assist-api/entities/period/periods';
import { isError, isInitialLoad } from 'utils/check-query-status';
import './period-badge.scss';

export type Props = {
  startDate: string;
  endDate: string;
  showInputDates?: boolean;
};

/**
 *  Display matching periods as a badge or badge list
 *  - fetches periods that include the provided date range
 *  - display a list if multiple possible periods
 *  - uses period info in a tooltip or provided date range info
 * @param showInputDates controls if the provided range id preferred as a tooltip then the range of the found period
 * @param startDate start of the range
 * @param endDate end of the range
 */
export default function PeriodBadge({ startDate, endDate, showInputDates }: Props): ReactElement {
  /* Get periods that the date range is within */
  const externalPeriods = usePeriods({
    filters: {
      'startDate[before]': startDate,
      'endDate[after]': endDate
    }
  });

  /* Get periods that are within the date range */
  const internalPeriods = usePeriods({
    filters: {
      'startDate[before]': endDate,
      'endDate[after]': startDate
    }
  });

  const periods = useMemo(
    () =>
      sortBy(
        uniqBy(
          [...(internalPeriods.data?.json || []), ...(externalPeriods.data?.json || [])],
          'id'
        ),
        'startDate'
      ),
    [externalPeriods.data?.json, internalPeriods.data?.json]
  );

  if (isInitialLoad(internalPeriods, externalPeriods)) {
    return <span className="skeleton-box loading" style={{ minWidth: '10ch' }} />;
  }
  if (isError(internalPeriods, externalPeriods)) {
    return <span className="skeleton-box error" style={{ minWidth: '10ch' }} />;
  }
  if (!periods.length) {
    return (
      <Badge>
        {format(new Date(startDate), commonDateFormat)} -{' '}
        {format(new Date(endDate), commonDateFormat)}
      </Badge>
    );
  }

  const id = uuid();
  const showInputRange =
    showInputDates &&
    !(
      isSameDay(new Date(periods[0]?.startDate), new Date(startDate)) &&
      isSameDay(new Date(periods[0]?.endDate), new Date(endDate))
    );

  return (
    <BadgeList id={id}>
      {periods.map((period) => (
        <span key={period.id} id={`${id}-${period.id}`} className="badge period-badge my-auto">
          {period.name}: {period.year}
          {!showInputRange && (
            <ControlledPopover
              target={
                (() => document.getElementById(`${id}-${period.id}`)) as unknown as HTMLElement
              }
              placement="bottom-start"
              trigger="hover focus"
              title="Term Dates"
            >
              <PopoverBody>
                {format(new Date(period.startDate), ordinalDateFormat)} -{' '}
                {format(new Date(period.endDate), ordinalDateFormat)}
                {!!period.description && (
                  <>
                    <hr />
                    <div dangerouslySetInnerHTML={{ __html: period.description }} />
                  </>
                )}
              </PopoverBody>
            </ControlledPopover>
          )}
        </span>
      ))}
      {showInputRange && (
        <ControlledPopover
          target={(() => document.getElementById(id)) as unknown as HTMLElement}
          placement="bottom-start"
          title="Date Range"
          trigger="hover focus"
          hideArrow
        >
          <PopoverBody>
            {format(new Date(startDate), ordinalDateFormat)} -{' '}
            {format(new Date(endDate), ordinalDateFormat)}
          </PopoverBody>
        </ControlledPopover>
      )}
    </BadgeList>
  );
}
